import type { UseFormReturn } from '@faceup/form'
import { Form } from '@faceup/ui'
import { RecipientsOnReportForm } from '@faceup/utils'
import type { ResultOf } from '@graphql-typed-document-node/core'
import { useId } from '@mantine/hooks'
import { useCallback, useEffect } from 'react'
import { defineMessages, FormattedMessage } from '../../../TypedIntl'
import type { fragments } from '../AF_CreateReportForm'
import type { Recipient } from '../hooks/useRecipients'
import { useRecipients } from '../hooks/useRecipients'
import { AF_Recipients } from './AF_Recipients'

const messages = defineMessages({
  recipientsLabel: 'FollowUp.report.recipients',
  recipientsDetail: 'FollowUp.report.recipientsDetail',
  recipientsDetailEditable: 'FollowUp.report.recipientsDetailEditable',
})

type AF_RecipientsFieldProps = {
  form: UseFormReturn
  categoryItemId?: string
  organizationalUnitItemId?: string
  reportSourceId: string
  recipientsOnReportForm: RecipientsOnReportForm
  institutionData: ResultOf<typeof fragments.AF_CreateReportForm_institution>
  formatMessage: (
    message: ReturnType<typeof defineMessages>[keyof ReturnType<typeof defineMessages>]
  ) => string
}

export const AF_RecipientsField = ({
  form,
  categoryItemId,
  organizationalUnitItemId,
  reportSourceId,
  recipientsOnReportForm,
  institutionData,
  formatMessage,
}: AF_RecipientsFieldProps) => {
  const id = useId()
  const ariaIds = { hintId: `${id}-hint` }

  const selectedCategoryValue = categoryItemId
    ? (form.watch(`${categoryItemId}.value`) as string | undefined)
    : undefined
  const selectedOrgUnitValue = organizationalUnitItemId
    ? (form.watch(`${organizationalUnitItemId}.value`) as string | undefined)
    : undefined

  const { recipients, areRecipientsLoading, changeRecipient } = useRecipients(
    selectedOrgUnitValue,
    selectedCategoryValue,
    reportSourceId,
    recipientsOnReportForm
  )

  useEffect(() => {
    form.setValue('recipients', recipients)
  }, [recipients, form])

  const handleRecipientChange = useCallback(
    (recipient: Recipient) => {
      changeRecipient(recipient)
    },
    [changeRecipient]
  )

  return (
    <Form.Item
      withAsterisk
      label={<FormattedMessage {...messages.recipientsLabel} />}
      hint={formatMessage(
        recipientsOnReportForm === RecipientsOnReportForm.Editable
          ? messages.recipientsDetailEditable
          : messages.recipientsDetail
      )}
      ariaIds={ariaIds}
      formItemProps={{
        'data-test': 'create-company-report-recipients',
      }}
    >
      <AF_Recipients
        selectedOrganizationUnit={selectedOrgUnitValue}
        selectedCategory={selectedCategoryValue}
        recipients={recipients}
        changeRecipient={handleRecipientChange}
        areRecipientsLoading={areRecipientsLoading}
        institution={institutionData}
        hintId={ariaIds.hintId}
      />
    </Form.Item>
  )
}
